import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Transaction {
  id: string;
  date: string;
  listing: string;
  renter: string;
  amount: number;
  host_payout: number;
  status: 'paid' | 'pending' | 'processing';
  type: 'rental' | 'deposit' | 'refund';
}

export default function EarningsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'year' | 'all'>('all');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState<string | null>(null);

  useEffect(() => {
    const fetchEarnings = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);

        // Get user's listing IDs
        const { data: userListings } = await supabase
          .from('listings')
          .select('id')
          .eq('host_id', user.id);

        const listingIds = userListings?.map(l => l.id) || [];

        if (listingIds.length === 0) {
          setTransactions([]);
          setLoading(false);
          return;
        }

        // Fetch bookings where user is the host
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select(`
            id,
            created_at,
            total_amount,
            host_payout,
            platform_fee,
            status,
            months,
            renter_id,
            listing_id,
            listings:listing_id (
              id,
              title
            )
          `)
          .in('listing_id', listingIds)
          .in('status', ['active', 'confirmed', 'completed'])
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Fetch renter profiles
        const renterIds = [...new Set((bookings || []).map(b => b.renter_id))];
        let renterProfiles: Record<string, string> = {};

        if (renterIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', renterIds);

          if (profiles) {
            renterProfiles = profiles.reduce((acc, p) => {
              acc[p.id] = p.full_name || 'Guest';
              return acc;
            }, {} as Record<string, string>);
          }
        }

        // Convert bookings to transactions
        const realTransactions: Transaction[] = (bookings || []).map(booking => ({
          id: booking.id,
          date: booking.created_at,
          listing: booking.listings?.title || 'Unknown Listing',
          renter: renterProfiles[booking.renter_id] || 'Guest',
          amount: booking.total_amount || 0,
          host_payout: booking.host_payout || booking.total_amount || 0,
          status: 'paid',
          type: 'rental' as const,
        }));

        setTransactions(realTransactions);
      } catch (_error: unknown) {
        // silently handle error
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [user?.id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectParam = params.get('connect');
    if (connectParam === 'success') {
      setConnectStatus('success');
    } else if (connectParam === 'refresh') {
      setConnectStatus('refresh');
    }
  }, []);

  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const filteredByPeriod = transactions.filter(t => {
    const d = new Date(t.date);
    if (selectedPeriod === 'week') return d >= startOfWeek;
    if (selectedPeriod === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (selectedPeriod === 'year') return d.getFullYear() === now.getFullYear();
    return true;
  });

  const totalEarnings = transactions
    .filter(t => t.status === 'paid')
    .reduce((sum, t) => sum + t.host_payout, 0);

  const thisMonthEarnings = transactions
    .filter(t => {
      const d = new Date(t.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && t.status === 'paid';
    })
    .reduce((sum, t) => sum + t.host_payout, 0);

  const pendingEarnings = transactions
    .filter(t => t.status === 'pending' || t.status === 'processing')
    .reduce((sum, t) => sum + t.host_payout, 0);

  const getStatusBadge = (status: string) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
    };
    return styles[status as keyof typeof styles];
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      paid: 'ri-checkbox-circle-line',
      pending: 'ri-time-line',
      processing: 'ri-loader-4-line',
    };
    return icons[status as keyof typeof icons];
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      rental: 'ri-home-4-line',
      deposit: 'ri-safe-line',
      refund: 'ri-refund-line',
    };
    return icons[type as keyof typeof icons];
  };

  const handleConnectBank = async () => {
    setConnectLoading(true);
    setConnectStatus(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-connect-account', {
        body: { userId: user?.id, email: user?.email }
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Connect error:', err);
      setConnectStatus('error');
    } finally {
      setConnectLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-4 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Earnings</h1>
            <p className="text-gray-600 mt-2">Track your rental income and transactions</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-2xl"></i>
                </div>
                <i className="ri-arrow-up-line text-2xl"></i>
              </div>
              <p className="text-green-100 text-sm mb-1">Total Earnings</p>
              <p className="text-4xl font-bold">${totalEarnings.toLocaleString()}</p>
              <p className="text-green-100 text-sm mt-2">All time</p>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-calendar-line text-2xl"></i>
                </div>
                <i className="ri-line-chart-line text-2xl"></i>
              </div>
              <p className="text-blue-100 text-sm mb-1">This Month</p>
              <p className="text-4xl font-bold">${thisMonthEarnings.toLocaleString()}</p>
              <p className="text-blue-100 text-sm mt-2">{new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}</p>
            </div>

            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-2xl"></i>
                </div>
                <i className="ri-hourglass-line text-2xl"></i>
              </div>
              <p className="text-yellow-100 text-sm mb-1">Pending</p>
              <p className="text-4xl font-bold">${pendingEarnings.toLocaleString()}</p>
              <p className="text-yellow-100 text-sm mt-2">Processing</p>
            </div>
          </div>

          {/* Period Filter */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 mb-6 flex flex-wrap gap-2">
            {(['week', 'month', 'year', 'all'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`flex-1 min-w-[70px] px-3 md:px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm ${
                  selectedPeriod === period
                    ? 'bg-[#14B8A6] text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {period === 'week' && 'This Week'}
                {period === 'month' && 'This Month'}
                {period === 'year' && 'This Year'}
                {period === 'all' && 'All Time'}
              </button>
            ))}
          </div>

          {/* Transactions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Recent Transactions</h2>
              <button className="flex items-center gap-2 px-4 py-2 text-[#14B8A6] hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-download-line"></i>
                <span className="font-semibold">Export</span>
              </button>
            </div>

            <div className="overflow-x-auto w-full">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                      Type
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Listing
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Renter
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <span className="hidden sm:inline">Status</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredByPeriod.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-3">
                        <p className="text-sm text-gray-900">
                          {new Date(transaction.date).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </td>
                      <td className="px-3 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-2">
                          <i className={`${getTypeIcon(transaction.type)} text-gray-600`}></i>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {transaction.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-sm font-medium text-gray-900">{transaction.listing}</p>
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-sm text-gray-900">{transaction.renter}</p>
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-sm font-bold text-gray-900">
                          ${transaction.host_payout.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </td>
                      <td className="px-2 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(transaction.status)}`}>
                          <i className={`${getStatusIcon(transaction.status)} hidden sm:inline`}></i>
                          {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bank Account Connection */}
          <div className="mt-6 bg-white border-2 border-[#14B8A6] rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-bank-line text-[#14B8A6] text-xl"></i>
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-1">Payout Bank Account</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Connect your Australian bank account to receive payouts from your Stashly rentals. Payouts are processed via Stripe Connect.
                </p>
                {connectStatus === 'success' && (
                  <div className="flex items-center gap-2 text-sm text-green-600 mb-3 bg-green-50 px-3 py-2 rounded-lg">
                    <i className="ri-checkbox-circle-line"></i>
                    <span>Bank account connected successfully! You can now receive payouts.</span>
                  </div>
                )}
                {connectStatus === 'refresh' && (
                  <div className="flex items-center gap-2 text-sm text-yellow-600 mb-3 bg-yellow-50 px-3 py-2 rounded-lg">
                    <i className="ri-error-warning-line"></i>
                    <span>Onboarding session expired. Please try connecting again.</span>
                  </div>
                )}
                {connectStatus === 'error' && (
                  <div className="flex items-center gap-2 text-sm text-red-600 mb-3 bg-red-50 px-3 py-2 rounded-lg">
                    <i className="ri-error-warning-line"></i>
                    <span>Something went wrong. Please try again or contact support@stashly.com.au</span>
                  </div>
                )}
                <button
                  onClick={handleConnectBank}
                  disabled={connectLoading}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9C8E] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  <i className="ri-bank-line"></i>
                  {connectLoading ? 'Connecting...' : 'Connect Bank Account via Stripe'}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  You will be redirected to Stripe&apos;s secure onboarding. Your bank details are never stored on Stashly.
                </p>
              </div>
            </div>
          </div>

          {/* Payout Information */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-blue-900 mb-2">Payout Information</h4>
                <p className="text-sm text-blue-800 mb-3">
                  Earnings are transferred to your linked bank account within 3-5 business days of each completed booking payment. Payouts are processed via Stripe Connect.
                </p>
                <button className="text-sm font-semibold text-blue-600 hover:text-blue-700 cursor-pointer">
                  Manage Payout Settings →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
